/**
 * "Your open orders on this outcome" under the order ticket (#678): both sides of one outcome, each with Cancel,
 * plus "Cancel all" (one action, one signature). Rendered only for your own connected wallet (not ghost mode).
 */
import { useMemo } from 'react';
import { useOpenOrders } from '../account/orders/hooks';
import { formatPx } from '../account/orders/labels';
import { fromCoin } from '../hl/outcomes';
import { useViewedAccount, useWallet } from '../wallet/context';
import { useCancelOrders } from './useCancelOrders';
import { localizeNumber } from '../i18n/format';
import { useT } from '../i18n/useT';

export interface MarketOpenOrdersProps {
  outcome: number;
  sideNames: readonly [string, string];
}

function Inner({ outcome, sideNames, address }: MarketOpenOrdersProps & { address: string }) {
  const { orders, loading } = useOpenOrders(address);
  const t = useT();
  const cancel = useCancelOrders();
  const mine = useMemo(() => orders.filter((o) => fromCoin(o.coin)?.outcome === outcome), [orders, outcome]);
  const cancellable = mine.filter((o) => !cancel.rowState(o.oid)?.canceling);
  const disabled = !!cancel.disabledReason || cancel.busy;

  return (
    <section aria-label={t('ticket.open_orders.label')} className="card mt-4 space-y-2 p-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold">
          {t('ticket.open_orders.title')} <span className="text-muted">({mine.length})</span>
        </h2>
        {mine.length > 1 && (
          <span title={cancel.disabledReason ?? undefined}>
            <button
              type="button"
              disabled={disabled || cancellable.length === 0}
              onClick={() => cancel.cancel(cancellable)}
              className="rounded border border-border px-2 py-0.5 text-xs hover:border-danger hover:text-danger disabled:opacity-50"
            >
              {t('ticket.open_orders.cancel_all', { n: cancellable.length })}
            </button>
          </span>
        )}
      </div>
      {cancel.notice && (
        <p role="status" className="text-xs text-warn" data-testid="cancel-notice">
          {cancel.notice}
        </p>
      )}
      {loading ? (
        <p className="text-xs text-muted">{t('common.loading')}</p>
      ) : mine.length === 0 ? (
        <p className="text-xs text-muted">{t('ticket.open_orders.none')}</p>
      ) : (
        <ul className="divide-y divide-border text-xs">
          {mine.map((o) => {
            const ref = fromCoin(o.coin)!;
            const st = cancel.rowState(o.oid);
            const filled = Number(o.origSz) - Number(o.sz);
            return (
              <li key={o.oid} className="flex items-center gap-2 py-1.5" data-testid="market-open-order">
                <div className="min-w-0 flex-1">
                  <div>
                    <span className={o.side === 'B' ? 'text-yes' : 'text-no'}>{t(o.side === 'B' ? 'ticket.buy' : 'ticket.sell')}</span>{' '}
                    <span className={ref.side === 0 ? 'text-yes' : 'text-no'} translate="no">
                      {sideNames[ref.side]}
                    </span>{' '}
                    <span className="num">
                      {localizeNumber(String(Number(o.sz)))} @ {formatPx(o.limitPx, 'outcome')}
                    </span>
                  </div>
                  {filled > 0 && (
                    <div className="num text-muted">
                      {t('ticket.status.part_filled', { filled: localizeNumber(String(filled)), orig: localizeNumber(String(Number(o.origSz))) })}
                    </div>
                  )}
                  {st?.error && (
                    <div className="text-danger" data-testid="cancel-error">
                      {st.error}
                    </div>
                  )}
                </div>
                {st?.canceling ? (
                  <span className="text-muted" role="status" data-testid="cancel-pending">
                    {t('cancel.canceling')}
                  </span>
                ) : (
                  <span title={cancel.disabledReason ?? undefined}>
                    <button
                      type="button"
                      disabled={disabled}
                      onClick={() => cancel.cancel([o])}
                      aria-label={t('cancel.button_aria', { oid: String(o.oid) })}
                      className="rounded border border-border px-2 py-0.5 hover:border-danger hover:text-danger disabled:opacity-50"
                    >
                      {t('cancel.button')}
                    </button>
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {cancel.dialog}
    </section>
  );
}

export default function MarketOpenOrders(props: MarketOpenOrdersProps) {
  const wallet = useWallet();
  const viewed = useViewedAccount();
  const own = wallet.status === 'connected' ? wallet.address : null;
  if (!viewed.canTrade || !own) return null;
  return <Inner {...props} address={own} />;
}
